import React, { useMemo } from 'react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import { okaidia } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useExperiment } from '~/systems/experiment';
import { useUiState } from '~/systems/uiState';
import { experimentToPulser } from '~/util/codegen';

import './Code.css';

SyntaxHighlighter.registerLanguage('python', python);

interface CodeProps {
  headerNode?: HTMLElement | null;
}

export default function Code(_props: CodeProps) {
  const experiment = useExperiment();
  const uiState = useUiState();

  const codeLines = useMemo(
    () =>
      experiment.commitedState
        ? experimentToPulser(experiment.commitedState)
        : [],
    [experiment.commitedState]
  );

  const codeText = useMemo(() => {
    return codeLines.map((l) => l.content).join('\n');
  }, [codeLines]);

  // Highlight corresponding lines when an atom or a pulse is selected
  const highlightedLines = useMemo(() => {
    const type = uiState.atomSelection.size > 0 ? 'atom' : 'pulse';
    const activeUuids =
      type === 'atom' ? uiState.atomSelection : uiState.pulseSelection;
    // react-syntax-highlighter line numbers are 1-based
    return codeLines.reduce((acc: number[], line, i) => {
      if (
        line.metas.some(
          (m) => m.meta.kind === type && activeUuids.has(m.meta.uuid)
        )
      ) {
        return [...acc, i + 1];
      }
      return acc;
    }, []);
  }, [codeLines, uiState.atomSelection, uiState.pulseSelection]);

  const lineProps = (lineNumber: number) => {
    const style: React.CSSProperties = {};
    if (highlightedLines.includes(lineNumber)) {
      style.backgroundColor = 'rgb(248 212 65 / 20%)';
    }
    return { style };
  };

  return (
    <div className="Code">
      <SyntaxHighlighter
        className="u-scrollbarDark line-numbers language-python"
        language="python"
        style={okaidia}
        customStyle={{
          height: '100%', // Needed to display scrollbars
          margin: 0,
          borderRadius: 0,
          fontFamily: 'monospace',
          fontSize: '13px',
          lineHeight: 2,
          background: 'var(--surface-neutral-secondary)'
        }}
        showLineNumbers
        wrapLines
        lineProps={lineProps}
      >
        {codeText}
      </SyntaxHighlighter>
    </div>
  );
}
